import { use, useContext, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, CopyTextBtn, ModuleBase, UserContext } from '@/components';
import ReportModulHeader from '@/components/report/ReportModulHeader';
import { ModuleBaseModel, ModuleBasePropsModel, UserRole } from '@/types';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import CaretDownLight from '@/assets/caretDownLight.svg';
import { motion } from 'motion/react';
import { accordionPanel } from '@/animations';
import { useRouter } from 'next/router';

type FutureInsightsModuleModel = ModuleBaseModel<
  {
    futureInsights: {
      content: string;
      title: string;
    }[];
  } & ModuleBasePropsModel
>;

const FutureInsightsModule = (data: FutureInsightsModuleModel) => {
  const {
    props: { futureInsights },
  } = data;
  const t = useTranslations();

  const [activeIndexList, setActiveIndexList] = useState<number[]>([0]);
  const router = useRouter();
  const { roles } = useContext(UserContext);
  const showCopyBtns = useMemo(() => roles?.includes(UserRole.Staff) ?? false, [roles]);

  useEffect(() => {
    restoreActiveIndex();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (index: number) => {
    let newArray = [...activeIndexList];
    if (newArray.includes(index)) {
      newArray = newArray.filter((i) => i !== index);
    } else {
      newArray = [index, ...newArray];
    }
    setActiveIndexList(newArray);
    saveActiveIndex(index);
  };

  const setActiveIndexListSingle = (index: number) => {
    setActiveIndexList([index]);
    saveActiveIndex(index);
  };

  const saveActiveIndex = (index: number) => {
    try {
      sessionStorage.setItem(`futureInsights-active-index:${router.asPath}`, index.toString());
    } catch {
      // ignore
    }
  };

  const restoreActiveIndex = () => {
    const activeIndex = sessionStorage.getItem(`futureInsights-active-index:${router.asPath}`);
    if (activeIndex && !router.query.tab && futureInsights && parseInt(activeIndex, 10) < futureInsights.length) {
      setActiveIndexList([parseInt(activeIndex, 10)]);
    }
  };

  const copyText = useMemo(() => {
    return futureInsights
      .map((futureInsight) => futureInsight.title + '\n\n' + futureInsight.content?.replace(/<[^>]+>/g, '').trim())
      .join('\n\n');
  }, [futureInsights]);

  const copyHtmlContent = useMemo(() => {
    return futureInsights.map((futureInsight) => `${futureInsight.title}${futureInsight.content}`).join('<br>');
  }, [futureInsights]);

  return (
    <ModuleBase data={{ ...data, props: { ...data.props, anchor: 'report-future-insights' } }}>
      <ReportModulHeader
        expanded
        title={t('report.$futureInsights')}
        className="relative container"
        saveOpenStateKey="futureInsightsModule"
      >
        {
          // Mobile View
          futureInsights.map((futureInsight, index) => (
            <div
              key={index}
              className="flex flex-col justify-between pt-5 md:hidden md:flex-row md:gap-6 md:pt-10 xl:pt-12 print:flex-row"
            >
              <div
                className={classNames(
                  'border-from-bg/20 w-full md:w-1/4 md:pb-4 xl:w-1/6 print:w-1/4',
                  activeIndexList.includes(index) && 'border-b pb-5 md:border-b-0',
                )}
              >
                <button onClick={() => toggle(index)} className="flex w-full items-center justify-between text-left">
                  <h4
                    className={classNames(
                      activeIndexList.includes(index) ? 'scale-100 font-light' : 'scale-75',
                      'origin-left text-lg transition-all lg:leading-[30px] print:text-2xl print:leading-[30px]',
                    )}
                  >
                    {futureInsight.title}
                  </h4>
                  <CaretDownLight
                    className={classNames(
                      'h-4 w-4 shrink-0 transition-all md:hidden print:hidden',
                      activeIndexList.includes(index) && 'rotate-180',
                    )}
                  />
                </button>
              </div>

              <motion.div
                variants={accordionPanel.variants}
                initial="inactive"
                animate={activeIndexList.includes(index) ? 'active' : 'inactive'}
                className="w-full overflow-hidden md:!h-auto md:w-3/4 md:!opacity-100 print:!h-auto print:!opacity-100"
              >
                <div className="flex flex-col gap-6 pt-5 md:pt-0 lg:gap-3">
                  <div className="richText" dangerouslySetInnerHTML={{ __html: futureInsight.content }} />
                </div>
              </motion.div>
            </div>
          ))
        }

        {
          // Desktop View
        }
        <CopyTextBtn
          copyText={copyText}
          copyHtmlContent={copyHtmlContent}
          active={showCopyBtns}
          placement="right"
          className="hidden md:block"
          showCopiedFeedback
        >
          <div className="mt-12 hidden flex-row justify-between md:flex print:hidden">
            <div className="flex flex-col gap-4 md:w-4/12 md:pr-3 lg:w-3/12 lg:pr-6">
              {futureInsights.map((futureInsight, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndexListSingle(index)}
                  className={classNames('flex w-full cursor-pointer items-center justify-between text-left')}
                >
                  <h4
                    className={classNames(
                      'origin-left text-lg leading-6 font-light transition-all xl:text-[26px] xl:leading-8',
                      activeIndexList.includes(index) ? 'scale-100' : 'scale-75 opacity-50',
                    )}
                  >
                    {futureInsight.title}
                  </h4>
                </button>
              ))}
            </div>

            <div className="relative w-8/12 lg:w-9/12">
              <AnimatePresence>
                {futureInsights[activeIndexList[0]] && (
                  <motion.div
                    initial={{ x: 5, opacity: 0 }}
                    animate={{
                      x: 0,
                      opacity: 1,
                      transition: {
                        delay: 0.2,
                        type: 'spring',
                        damping: 25,
                      },
                    }}
                    exit={{ x: 5, opacity: 0, position: 'absolute' }}
                    transition={{
                      type: 'tween',
                      ease: 'easeIn',
                      duration: 0.2,
                    }}
                    key={futureInsights[activeIndexList[0]].title}
                    className="flex flex-col gap-6 lg:pr-16"
                  >
                    <div className="flex flex-col gap-6 pt-5 md:pt-0 lg:gap-3">
                      <div
                        className="richText !font-light"
                        dangerouslySetInnerHTML={{ __html: futureInsights[activeIndexList[0]].content }}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </CopyTextBtn>
      </ReportModulHeader>
    </ModuleBase>
  );
};

export default FutureInsightsModule;
